import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, mergeBones, stages, boneG } from './kit';
/* ============================================================
   ZWILLING 雙生 —— BOSS・複合雙體
   ============================================================

   剪影：兩具分離本體＋能量鏈
   階段：'P1 分離・各打各的（紅＋橙）','P2 靠攏・中間拉出能量鏈','P3 合體・核心露出（紫）'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ══════════════════════════════════════════════════════════════
   BOSS 08–17 —— 三條血量。造型端一律寫成 s1/s2 的線性組合。
   共通設計原則：階段轉換要「換掉玩家的問題」，不是「把數字調大」。
   ══════════════════════════════════════════════════════════════ */

/* ── BOSS-08 ZWILLING 雙生 ── 複合式：兩具獨立本體共用一條命。
   P1 分頭進攻（左紅近身 / 右橙遠射）→ 你得先決定打誰。
   P2 兩具靠攏，中間拉出一條能量鏈 —— 之前的安全地帶變成最危險的地方。
   P3 合體成單一巨體，核心露出。體積翻倍，但目標只剩一個。 */
export function buildZwilling(){
  var GL = glowMat(TINT.red.hex, 2.0), GLA = glowMat(TINT.amb.hex, 2.1),
      GLV = glowMat(TINT.vio.hex, 2.5);
  var root = new THREE.Group(), i, s;
  /* A：紅・粗矮・雙拳 */
  var A = boneG(root, -3.40, 2.20, 0);
  A.add(bevel(M.dark,  2.20, 1.80, 1.90, [0,0,0]));
  A.add(plate(M.armor, 2.40, 0.76, 1.05, [0, 0.58, 0.56], -0.36));
  A.add(rib  (M.armor, 2.46, 0.12, 0.12, [0, 0.88, 0.30], -0.36));
  A.add(mesh (G.cone,  M.armor, [0.80,1.30,0.80], [0,-1.30, 0.20], [Math.PI,0,0]));
  var armL = [];
  for (i = 0; i < 2; i++){
    s = i ? -1 : 1;
    var ar = boneG(A, s*1.30, 0.40, 0.10);
    ar.add(mesh(G.cyl, M.dark,  [0.52,1.30,0.52], [0,-0.60,0]));
    ar.add(mesh(G.box, M.armor, [0.90,0.90,1.00], [0,-1.35,0.10]));
    var fg = mesh(G.box, GL, [0.70,0.12,0.16], [0,-1.35,0.62]);
    fg.userData.noMerge = 1; ar.add(fg);
    armL.push(ar);
  }
  /* B：橙・細長・長砲 */
  var B = boneG(root, 3.40, 2.40, 0);
  B.add(bevel(M.dark,  1.40, 2.30, 1.60, [0,0,0]));
  B.add(plate(M.armor, 1.56, 0.90, 0.90, [0, 0.80, 0.46], -0.34));
  B.add(rib  (M.armor, 0.12, 2.20, 0.12, [ 0.72, 0, 0.34]));
  B.add(rib  (M.armor, 0.12, 2.20, 0.12, [-0.72, 0, 0.34]));
  B.add(mesh (G.cone,  M.armor, [0.62,1.50,0.62], [0,-1.70, 0.10], [Math.PI,0,0]));
  var bar = boneG(B, 0, 0.70, 0);
  bar.add(mesh(G.cyl, M.dark,  [0.46,3.20,0.46], [0,0,1.30], [HP,0,0]));
  bar.add(mesh(G.tor, M.trim,  [0.90,0.90,0.50], [0,0,2.40]));
  var bmz = mesh(G.tor, GLA, [0.76,0.76,0.55], [0,0,2.86]);
  bmz.userData.noMerge = 1; bar.add(bmz);
  /* 能量鏈與合體核心 */
  var chain = mesh(G.cyl, GLV, [0.30,6.80,0.30], [0,2.30,0], [0,0,HP]);
  chain.userData.noMerge = 1; chain.visible = false; root.add(chain);
  var core = boneG(root, 0, 2.30, 0);
  var cm = mesh(G.oct, GLV, [1.70,1.70,1.70], [0,0,0]);
  cm.userData.noMerge = 1; core.add(cm);
  core.add(mesh(G.tor, M.trim, [2.60,2.60,0.36], [0,0,0], [HP,0,0]));
  core.visible = false;

  root.userData.rig = {A:A, B:B, arms:armL, bar:bar, chain:chain, core:core, cm:cm,
                       GL:GL, GLA:GLA, GLV:GLV};
  return mergeBones(root);
}
export function updZwilling(rig, o, dt){
  stages(rig, o, dt, 2.0);
  var s1 = rig.s1, s2 = rig.s2, i;
  var gap = 3.40 - 1.30*s1 - 1.42*s2;              // 3.40 → 2.10 → 0.68
  rig.A.position.x = -gap; rig.B.position.x = gap;
  rig.A.position.y = 2.20 + 0.10*Math.sin(o.t*1.3) - 0.20*s2;
  rig.B.position.y = 2.40 + 0.12*Math.sin(o.t*1.1 + 1.5) - 0.20*s2;
  rig.A.rotation.y =  0.30*s1*Math.sin(o.t*0.8) + 0.40*s2;
  rig.B.rotation.y = -0.30*s1*Math.sin(o.t*0.8) - 0.40*s2;
  rig.A.scale.setScalar(1 + 0.16*s2); rig.B.scale.setScalar(1 + 0.16*s2);
  for (i = 0; i < 2; i++)
    rig.arms[i].rotation.x = -0.30 + o.tel*0.75 - o.atk*1.40 + 0.18*Math.sin(o.t*1.6 + i*3);
  rig.bar.rotation.x = 0.10 - o.tel*0.24 + o.atk*0.30;
  rig.chain.visible = s1 > 0.25 && s2 < 0.7;
  rig.chain.scale.set(0.30*(0.4+s1), gap*2, 0.30*(0.4+s1));
  rig.core.visible = s2 > 0.3;
  rig.cm.scale.setScalar(s2*(1 + 0.12*Math.sin(o.t*3) + 0.45*o.tel));
  rig.core.rotation.y += dt*1.4;
  rig.GL.emissiveIntensity  = 2.0 + 2.4*o.tel + 5*o.atk*(1-o.atk);
  rig.GLA.emissiveIntensity = 2.1 + 2.6*o.tel + 5*o.atk*(1-o.atk);
  rig.GLV.emissiveIntensity = 1.0 + 2.0*s1 + 2.8*s2;
}